import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, Spin, Empty, Button, Tag, Input, message, Tooltip } from "antd";
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ApartmentOutlined,
  RocketOutlined,
  SearchOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { AI_TIMEOUT } from "api/aiLoading";
import ApplyJobModal from "./ApplyJobModal";
import { EmploymentTypeEnumLabel } from "Constants/Enums/EmploymentTypeEnum";

const ACCENT = "#0f5a2a";
const ACCENT_LIGHT = "#1e8c45";

function JobOpeningsList({ onApplied }) {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [applyTarget, setApplyTarget] = useState(null);

  const [eligibility, setEligibility] = useState({ isEligible: true, reason: "" });
  // Per Mudassir (May-22 update #2): backend now returns
  // isAiRecommendationAvailable on the /employee/explore response. When AI
  // is down it returns true postings + null AI fields per posting + this
  // flag set to false. Surface "AI temporarily unavailable" so employees
  // know matching scores aren't being computed right now.
  const [isAiRecommendationAvailable, setIsAiRecommendationAvailable] = useState(true);
  // Per Sara (May-23): distinguish "no jobs available" from "API failed" so
  // the employee can retry instead of staring at an empty list.
  const [loadError, setLoadError] = useState(false);

  const fetchPostings = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      // Explore endpoint runs AI-based recommendation per employee, so use a
      // longer timeout than the default 30s.
      const res = await axiosInstance.get(API.hiringJobPosting.employeeExplore, {
        timeout: AI_TIMEOUT,
      });
      const data = res.data;
      // Real backend shape: { isEligible, reasonMessage,
      //                       isAiRecommendationAvailable, recommendations: [...] }
      const list = Array.isArray(data)
        ? data
        : data?.recommendations || data?.postingDetail || data?.postings || [];
      setPostings(Array.isArray(list) ? list : []);
      setEligibility({
        isEligible: data?.isEligible !== false,
        reason: data?.reasonMessage || "",
      });
      setIsAiRecommendationAvailable(data?.isAiRecommendationAvailable !== false);
    } catch (err) {
      console.error("Error fetching employee job openings:", err);
      if (err.response?.status !== 404) {
        setLoadError(true);
      }
      setPostings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPostings();
  }, [fetchPostings]);

  const handleApplySuccess = () => {
    setApplyTarget(null);
    fetchPostings();
    if (onApplied) onApplied();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  const filtered = postings.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(p.jobTitle || "").toLowerCase().includes(q) ||
      String(p.departmentName || p.department || "").toLowerCase().includes(q) ||
      String(p.jobLocationType || "").toLowerCase().includes(q) ||
      String(p.jobType || "").toLowerCase().includes(q) ||
      String(EmploymentTypeEnumLabel[p.employmentType] || "").toLowerCase().includes(q)
    );
  });

  return (
    <div>
      {!eligibility.isEligible && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderLeft: "4px solid #ef4444",
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 14,
            color: "#991b1b",
            fontSize: "0.88rem",
          }}
        >
          ⚠ You{"'"}re not currently eligible to apply for internal roles.
          {eligibility.reason ? ` ${eligibility.reason}` : ""}
        </div>
      )}

      {/* AI-down banner: postings are still returned, but match scores +
          matched/missing skills + AI reasoning come back null. Tell the
          employee why those fields are missing instead of leaving them to
          guess. (Mudassir May-22 update #2.) */}
      {isAiRecommendationAvailable === false && eligibility.isEligible && (
        <div
          style={{
            background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
            border: "1px solid #fcd34d",
            borderLeft: "4px solid #f59e0b",
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontSize: "1.3rem" }}>🤖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#92400e" }}>
              AI matching temporarily unavailable
            </div>
            <div style={{ fontSize: "0.78rem", color: "#7c5e10", lineHeight: 1.4, marginTop: 2 }}>
              {eligibility.reason ||
                "Personalized fit scores and skill matching aren't being generated right now. The roles below are still open — please check back shortly for AI recommendations."}
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Search by title, department, or location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360, borderRadius: 10 }}
        />
        {isAiRecommendationAvailable && (
          <div
            style={{
              background: "#eafbf0",
              color: ACCENT,
              border: "1px solid #c6ecd2",
              padding: "6px 14px",
              borderRadius: 40,
              fontSize: "0.78rem",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <ThunderboltFilled /> AI-matched openings
          </div>
        )}
      </div>

      {loadError ? (
        // Per Sara (May-23): distinct error state with retry — distinguishes
        // "API failed" from the empty "no jobs available" state.
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderLeft: "4px solid #ef4444",
            borderRadius: 14,
            padding: "24px 28px",
            textAlign: "center",
            maxWidth: 480,
            margin: "40px auto",
          }}
        >
          <div style={{ fontSize: "2rem", marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: "1rem", fontWeight: 600, color: "#991b1b", marginBottom: 6 }}>
            Couldn&apos;t load job openings
          </div>
          <div
            style={{
              fontSize: "0.85rem",
              color: "#7c2d2d",
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            Something went wrong fetching the latest roles. Please try again.
          </div>
          <Button
            type="primary"
            onClick={fetchPostings}
            style={{
              background: ACCENT,
              borderColor: ACCENT,
              borderRadius: 40,
              fontWeight: 600,
              paddingLeft: 22,
              paddingRight: 22,
            }}
          >
            Try Again
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          description={
            postings.length === 0
              ? "No matching openings right now."
              : "No openings match your search."
          }
          style={{ padding: "40px 0" }}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((p) => (
            <PostingCard key={p.id || p.jobPostingId} posting={p} onApply={() => setApplyTarget(p)} />
          ))}
        </div>
      )}

      {applyTarget && (
        <ApplyJobModal
          posting={applyTarget}
          open={!!applyTarget}
          onClose={() => setApplyTarget(null)}
          onSuccess={handleApplySuccess}
        />
      )}
    </div>
  );
}

JobOpeningsList.propTypes = {
  onApplied: PropTypes.func,
};

function PostingCard({ posting, onApply }) {
  const title = posting.jobTitle || "Open Role";
  const department = posting.departmentName || posting.department || "Department";
  const location = posting.jobLocationType || "";
  const jobType = posting.jobType || "";
  const employmentType = EmploymentTypeEnumLabel[posting.employmentType] ?? "";
  const experience = posting.maximumExperience || posting.experienceLevel || "";
  const salary = posting.salaryRange || posting.salary || posting.compensation;
  const isApplied = Boolean(posting.isApplied || posting.applied);
  // Per Mudassir (May-23): backend standardized to `fitScore` across the 3
  // matching endpoints (explore-careers, alternatives, ai-evaluation). Keep
  // legacy field names as fallbacks for resilience.
  const matchPercentage =
    posting.fitScore ?? posting.matchPercentage ?? posting.matchScore;
  const isGoodFit = posting.isGoodFit;
  const careerPathFit = posting.careerPathFit;
  const matchedSkills = Array.isArray(posting.matchedSkills) ? posting.matchedSkills : [];
  const missingSkills = Array.isArray(posting.missingSkills) ? posting.missingSkills : [];
  const reason = posting.reason;

  // Color the match badge by fit
  const matchBg = isGoodFit
    ? `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)`
    : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)";

  return (
    <Card
      style={{
        borderRadius: 18,
        borderLeft: `5px solid ${isGoodFit ? ACCENT : "#9ca3af"}`,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
      styles={{ body: { padding: 18 } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: "#617e9e",
            }}
          >
            <ApartmentOutlined style={{ marginRight: 4 }} />
            {department}
          </div>
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: 700,
              color: ACCENT,
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
        </div>
        {matchPercentage != null && (
          <Tooltip title="AI match score based on your skills and profile">
            <div
              style={{
                background: matchBg,
                color: "white",
                padding: "4px 10px",
                borderRadius: 40,
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {Math.round(matchPercentage)}% match
            </div>
          </Tooltip>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginTop: 10,
          marginBottom: 12,
        }}
      >
        {location && (
          <Tag color="green" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            <EnvironmentOutlined /> {location}
          </Tag>
        )}
        {jobType && (
          <Tag color="blue" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            <ClockCircleOutlined /> {jobType}
          </Tag>
        )}
        {employmentType && (
          <Tag color="cyan" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            {employmentType}
          </Tag>
        )}
        {experience && (
          <Tag color="purple" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            {experience}
          </Tag>
        )}
        {salary && (
          <Tag color="gold" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            <DollarOutlined /> {salary}
          </Tag>
        )}
        {careerPathFit && (
          <Tag color="magenta" style={{ borderRadius: 40, fontSize: "0.72rem" }}>
            🎯 Career path fit
          </Tag>
        )}
      </div>

      {reason && (
        <div
          style={{
            fontSize: "0.82rem",
            color: "#3f4f63",
            background: "#f7faf8",
            border: "1px solid #e6efe8",
            borderLeft: `3px solid ${isGoodFit ? ACCENT : "#9ca3af"}`,
            padding: "10px 12px",
            borderRadius: 12,
            marginBottom: 12,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.45,
          }}
        >
          {reason}
        </div>
      )}

      {(matchedSkills.length > 0 || missingSkills.length > 0) && (
        <div style={{ marginBottom: 12 }}>
          {matchedSkills.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: "#0f5a2a",
                  marginBottom: 4,
                }}
              >
                ✓ Your matching skills
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {matchedSkills.slice(0, 6).map((s, i) => (
                  <span
                    key={`m-${i}`}
                    style={{
                      background: "#e6f4ea",
                      color: "#0f5a2a",
                      border: "1px solid #c6ecd2",
                      padding: "2px 9px",
                      borderRadius: 40,
                      fontSize: "0.72rem",
                      fontWeight: 500,
                    }}
                  >
                    {typeof s === "string" ? s : s?.skillName || s?.name || "Skill"}
                  </span>
                ))}
                {matchedSkills.length > 6 && (
                  <span style={{ fontSize: "0.72rem", color: "#5b6f8c", padding: "2px 4px" }}>
                    +{matchedSkills.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}
          {missingSkills.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: "#92400e",
                  marginBottom: 4,
                }}
              >
                ↑ Skills to develop
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {missingSkills.slice(0, 6).map((s, i) => (
                  <span
                    key={`x-${i}`}
                    style={{
                      background: "#fff7ed",
                      color: "#92400e",
                      border: "1px solid #fed7aa",
                      padding: "2px 9px",
                      borderRadius: 40,
                      fontSize: "0.72rem",
                      fontWeight: 500,
                    }}
                  >
                    {typeof s === "string" ? s : s?.skillName || s?.name || "Skill"}
                  </span>
                ))}
                {missingSkills.length > 6 && (
                  <span style={{ fontSize: "0.72rem", color: "#5b6f8c", padding: "2px 4px" }}>
                    +{missingSkills.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <Button
        type="primary"
        block
        size="middle"
        disabled={isApplied}
        onClick={onApply}
        style={{
          background: isApplied ? "#a3d9b3" : ACCENT,
          borderColor: isApplied ? "#a3d9b3" : ACCENT,
          borderRadius: 40,
          fontWeight: 600,
        }}
        icon={!isApplied && <RocketOutlined />}
      >
        {isApplied ? "Already Applied" : "Apply Now"}
      </Button>
    </Card>
  );
}

PostingCard.propTypes = {
  posting: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default JobOpeningsList;
